import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IOSModal } from '../ios/IOSModal';
import { IOSFormToggle } from '../ios/IOSFormGroup';

interface ProjectDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cascadeDelete: boolean) => Promise<void>;
  projectTitle: string;
  tasksCount: number;
}

/**
 * 项目删除确认模态框组件
 * 使用IOSModal作为基础，提供删除确认和级联删除选项
 */
export const ProjectDeleteModal: React.FC<ProjectDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  projectTitle,
  tasksCount
}) => {
  const { t } = useTranslation(['v3/projects', 'common']);
  const [cascadeDelete, setCascadeDelete] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // 处理确认删除
  const handleConfirm = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onConfirm(cascadeDelete);
      onClose();
    } catch (error) {
      console.error('删除项目失败:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // 底部按钮
  const footerContent = (
    <div className="flex flex-col gap-3">
      <button
        onClick={handleConfirm}
        disabled={isDeleting}
        className="w-full py-3 text-white text-ios-body font-medium rounded-ios-lg bg-ios-red dark:bg-ios-dark-red disabled:opacity-50"
      >
        {isDeleting ? t('common:actions.deleting') : t('v3/projects:confirmations.deleteProject')}
      </button>
      <button
        onClick={onClose}
        disabled={isDeleting}
        className="w-full py-3 text-ios-body font-medium rounded-ios-lg bg-ios-gray5 dark:bg-ios-dark-gray4"
      >
        {t('common:actions.cancel')}
      </button>
    </div>
  );

  return (
    <IOSModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('v3/projects:confirmations.deleteProject')}
      footerContent={footerContent}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center mb-2">
          <div className="w-12 h-12 text-ios-red dark:text-ios-dark-red">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <p className="text-ios-body text-center mb-2">
          {t('v3/projects:confirmations.deleteProjectDescription')}
        </p>

        <p className="text-ios-headline font-semibold text-center mb-4">
          {projectTitle}
        </p>

        {tasksCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-ios-body">
              {t('v3/projects:deleteModal.alsoDeleteTasks', { count: tasksCount })}
            </span>
            <IOSFormToggle
              checked={cascadeDelete}
              onChange={setCascadeDelete}
            />
          </div>
        )}
      </div>
    </IOSModal>
  );
};
